import { useCallback, useEffect, useMemo, useState } from 'react';

import { monitoringService, type BindingHealthRow } from '../services/monitoring';

export interface ConnectedBindingRef {
  bindingId: string;
  integrationId: string;
}

/**
 * Fetches per-binding integration health for Integrations / connected panels.
 * Refetches when the set of bindings (ids + types) changes or when `refetch()` is called.
 */
export function useConnectedBindingsHealth(
  spaceId: string | undefined,
  connectedBindings: ReadonlyArray<ConnectedBindingRef>
) {
  const requestKey = useMemo(
    () =>
      connectedBindings
        .map((c) => `${c.bindingId}:${c.integrationId}`)
        .sort()
        .join('|'),
    [connectedBindings]
  );

  const [bindingHealthById, setBindingHealthById] = useState<
    Record<string, BindingHealthRow>
  >({});
  const [windowHours, setWindowHours] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchNonce, setRefetchNonce] = useState(0);

  const refetch = useCallback(() => {
    setRefetchNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!spaceId || connectedBindings.length === 0) {
      setBindingHealthById({});
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const payload = connectedBindings.map((c) => ({
      binding_id: c.bindingId,
      integration_type: c.integrationId,
    }));

    void monitoringService
      .postBindingsHealth(spaceId, payload)
      .then((res) => {
        if (cancelled) return;
        setBindingHealthById(res.bindings ?? {});
        setWindowHours(res.window_hours ?? 1);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setBindingHealthById({});
        setError(
          err instanceof Error ? err.message : 'binding_health_failed'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [spaceId, requestKey, refetchNonce]);

  return {
    bindingHealthById,
    windowHours,
    loading,
    error,
    refetch,
  };
}
